import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { DialogEventFilter } from '@/features/DialogSession';
import { useTimeDisplayFormat } from '@/entities/settings';
import { formatTimeInterval } from '@/shared/lib/utils/string';
import { Chat } from '@/shared/ui/Chat';
import { TranscriptChatMessage } from './TranscriptChatMessage';
import { TranscriptPairedItem } from './TranscriptPairedItem';
import { TranscriptSystemMessage } from './TranscriptSystemMessage';
import { useIsContainerWide } from './useIsContainerWide';
import { useTranscriptData } from './useTranscriptData';
import { buildTranscriptMessages } from './utils';

/** Порог ширины, при котором перебитые реплики рисуются параллельно (две колонки). */
const PAIRED_LAYOUT_WIDTH = 700;

type Props = {
  dialogId: number;
  eventFilters?: DialogEventFilter[];
  /** Вид текста реплик; по умолчанию true (анонимизированный) */
  anonymized?: boolean;
};

export const DialogTranscription: FC<Props> = React.memo(
  ({ dialogId, eventFilters = ['pause', 'interruption'] as DialogEventFilter[], anonymized = true }) => {
    const { t } = useTranslation(['common', 'dialogs']);
    const timeDisplayFormat = useTimeDisplayFormat();
    // Метки реплик — интервалы времени: показываются в действующем формате системы
    const formatSeconds = useCallback(
      (totalSeconds: number) => formatTimeInterval(totalSeconds, timeDisplayFormat),
      [timeDisplayFormat],
    );
    const { data, isSuccess, isLoading, transcriptContainerRef } = useTranscriptData(dialogId, anonymized);

    const pairedLayout = useIsContainerWide(transcriptContainerRef, PAIRED_LAYOUT_WIDTH);

    const messages = useMemo(
      () => buildTranscriptMessages({ data, eventFilters, formatSeconds, t, pairedLayout }),
      [data, eventFilters, formatSeconds, t, pairedLayout],
    );

    return isSuccess || isLoading ? (
      <div ref={transcriptContainerRef} className="h-full DialogTranscription">
        {/* Боковые отступы одинаковы с обеих сторон: облака реплик обоих участников отстоят
            от своего края на одно расстояние. Их величина задана шириной оверлейного
            скроллбара справа — под него нужно свободное место */}
        <Chat key={dialogId} autoDownScroll={false} classNames={{ content: 'py-1 px-3' }}>
          {messages.map((item) => {
            if (item.type === 'pause') {
              return <TranscriptSystemMessage key={item.id} id={item.id} text={item.text} />;
            }
            if (item.type === 'paired') {
              return <TranscriptPairedItem key={item.id} item={item} />;
            }
            return <TranscriptChatMessage key={item.id} item={item} />;
          })}
        </Chat>
      </div>
    ) : (
      <p className={'p-4'}>{t('dialogs:noData')}</p>
    );
  },
);
